/**
 * mLearning — Chèn công thức LaTeX thẳng vào ô nội dung của H5P editor.
 *
 * ============================================================
 * VÌ SAO ĐẶT Ở REACT MÀ KHÔNG PHẢI ANGULAR
 * ============================================================
 * Kiến trúc hiện tại:
 *
 *     Angular (4200)
 *         └─ iframe ──► React app (3001)          <- KHÁC ORIGIN
 *                          └─ <H5PEditorUI>
 *                                └─ <h5p-editor>
 *                                      └─ iframe.h5p-editor-iframe  <- CÙNG origin với React
 *
 * Từ Angular không với tới ô soạn thảo của H5P được vì chặn ở tầng iframe
 * React. Nhưng iframe của H5P editor được tạo bằng `contentDocument.write()`
 * và KHÔNG có thuộc tính `src`, nên nó kế thừa origin của trang React.
 *
 * => Đặt ô soạn công thức ngay trong React app thì chèn thẳng được, không
 *    phải copy/dán, không phải dựng cầu postMessage.
 *
 * ============================================================
 * CÁCH HOẠT ĐỘNG
 * ============================================================
 * H5P giữ widget HTML đang được focus ở `H5PEditor.Html.current` (gán trong
 * h5peditor-html.js). Từ đó lấy instance CKEditor và chèn text vào con trỏ.
 *
 * Hỗ trợ cả hai đời CKEditor vì H5P Core đổi giữa hai bản:
 *   - Core 1.24 (h5p-server 9.3.x) dùng CKEditor 4  -> insertText()
 *   - Core 1.26 (h5p-server 10.x)  dùng CKEditor 5  -> model.insertContent()
 */

export type KetQuaChen =
    | 'da-chen'
    | 'khong-thay-editor'
    | 'chua-focus-o-nao'
    | 'loi';

/** Tìm cửa sổ của iframe H5P editor trong trang React. */
function layCuaSoIframeEditor(): Window | null {
    const iframe = document.querySelector(
        'h5p-editor iframe.h5p-editor-iframe, iframe.h5p-editor-iframe'
    ) as HTMLIFrameElement | null;

    if (!iframe) {
        return null;
    }
    try {
        return iframe.contentWindow ?? null;
    } catch {
        return null;
    }
}

/** Chèn text vào CKEditor, tự nhận biết đời 4 hay 5. */
function chenVaoCKEditor(ckeditor: any, text: string): boolean {
    // CKEditor 5: có model + editing
    if (ckeditor?.model && typeof ckeditor.model.change === 'function') {
        ckeditor.model.change((writer: any) => {
            ckeditor.model.insertContent(writer.createText(text));
        });
        ckeditor.editing?.view?.focus?.();
        return true;
    }

    // CKEditor 4: insertText tự escape ký tự HTML
    if (typeof ckeditor?.insertText === 'function') {
        ckeditor.insertText(text);
        ckeditor.focus?.();
        return true;
    }

    return false;
}

/** Dự phòng: ô input/textarea thuần đang focus trong iframe. */
function chenVaoOTextThuan(win: Window, text: string): boolean {
    const el = win.document.activeElement as
        | HTMLInputElement
        | HTMLTextAreaElement
        | null;

    if (!el) {
        return false;
    }
    const tag = el.tagName?.toLowerCase();
    if (tag !== 'input' && tag !== 'textarea') {
        return false;
    }

    const batDau = el.selectionStart ?? el.value.length;
    const ketThuc = el.selectionEnd ?? el.value.length;
    el.value = el.value.slice(0, batDau) + text + el.value.slice(ketThuc);

    const viTriMoi = batDau + text.length;
    el.setSelectionRange(viTriMoi, viTriMoi);

    // H5P lắng nghe 'change' để cập nhật params.
    // Dùng Event của chính iframe để sự kiện thuộc đúng realm.
    const EventCtor = (win as any).Event ?? Event;
    el.dispatchEvent(new EventCtor('input', { bubbles: true }));
    el.dispatchEvent(new EventCtor('change', { bubbles: true }));
    return true;
}

/**
 * Chèn chuỗi LaTeX (đã bọc `\( \)` hoặc `\[ \]`) vào ô đang có con trỏ.
 */
export function chenVaoOTextDangFocus(latexDaBoc: string): KetQuaChen {
    if (!latexDaBoc) {
        return 'loi';
    }

    const win = layCuaSoIframeEditor();
    if (!win) {
        return 'khong-thay-editor';
    }

    try {
        const H5PEditorNs: any = (win as any).H5PEditor;
        const widget = H5PEditorNs?.Html?.current;

        if (widget?.ckeditor && chenVaoCKEditor(widget.ckeditor, latexDaBoc)) {
            return 'da-chen';
        }

        if (chenVaoOTextThuan(win, latexDaBoc)) {
            return 'da-chen';
        }

        return 'chua-focus-o-nao';
    } catch (e) {
        console.error('[mLearning] Không chèn được công thức vào H5P:', e);
        return 'loi';
    }
}

/** Có ô nào trong H5P editor đang sẵn sàng nhận công thức không. */
export function sanSangChen(): boolean {
    const win = layCuaSoIframeEditor();
    if (!win) {
        return false;
    }
    try {
        return !!(win as any).H5PEditor?.Html?.current?.ckeditor;
    } catch {
        return false;
    }
}

/**
 * Bóc dấu bọc khỏi chuỗi có sẵn, để mở lại công thức cũ mà sửa.
 * Trả về LaTeX thuần và cho biết đó là kiểu block hay inline.
 */
export function boDauBoc(chuoi: string): { latex: string; block: boolean } {
    const s = (chuoi || '').trim();
    const cap: Array<[string, string, boolean]> = [
        ['\\(', '\\)', false],
        ['\\[', '\\]', true],
        ['$$', '$$', true]
    ];
    for (const [mo, dong, block] of cap) {
        // >= để bắt cả chuỗi rỗng như "\(\)" hoặc "$$$$",
        // nếu không sẽ bị bọc dấu hai lần.
        if (
            s.startsWith(mo) &&
            s.endsWith(dong) &&
            s.length >= mo.length + dong.length
        ) {
            return { latex: s.slice(mo.length, s.length - dong.length).trim(), block };
        }
    }
    return { latex: s, block: false };
}

/**
 * Dọn `\placeholder{...}` khỏi LaTeX trước khi đưa ra ngoài.
 *
 * `\placeholder` là ô trống của RIÊNG MathLive, MathJax trong H5P không biết
 * lệnh này nên nếu lọt ra bài giảng thì học sinh thấy chữ đỏ báo lỗi thay vì
 * công thức. Ô trống lọt ra theo hai đường: mẫu của mình có ô chưa điền, và
 * menu dựng sẵn của MathLive cũng tự chèn `\placeholder{}` — ảnh chụp của
 * giáo viên ngày 20/10 cho thấy `\(\overbrace{|A|}|\placeholder{}|\)`, tức
 * là lỗi này đã có từ trước khi thêm mẫu ô trống.
 *
 * Bỏ hẳn ô trống chứ không thay bằng dấu hỏi: `\widehat{}` hay `\frac{}{}`
 * đều là LaTeX hợp lệ và vẽ ra khung rỗng, nhìn vào biết ngay là còn thiếu.
 */
function donOTrong(latex: string): string {
    let truoc: string;
    let sau = latex;
    // Lặp vì placeholder có thể lồng nhau: \placeholder{\placeholder{}}
    do {
        truoc = sau;
        sau = truoc.replace(/\\placeholder(\[[^\]]*\])?\{\}/g, '');
    } while (sau !== truoc);
    // Ô trống còn nội dung bên trong thì giữ lại nội dung, bỏ vỏ.
    return sau.replace(/\\placeholder(\[[^\]]*\])?\{([^{}]*)\}/g, '$2');
}

/** Bọc LaTeX thuần bằng dấu để H5P.MathDisplay nhận diện. */
export function bocLatex(latexThuan: string, block: boolean): string {
    const s = donOTrong((latexThuan || '').trim()).trim();
    if (!s) {
        return '';
    }
    return block ? `\\[${s}\\]` : `\\(${s}\\)`;
}
